/**
 * V108-8-14 (journey audit, owner request 2026-10-18) — the first view of a program added after setup:
 * "What you already have, and what's next". Shown on the program page until its first period starts.
 *
 *   carry-over   what the workspace's existing sources already cover in THIS program (GET /coverage: the
 *                same count source as Setup and Today), as the shared coverage block
 *   next         the program's top open setup step (GET /enablers ranked; the first that unlocks one of its
 *                objectives), opened in Setup, plus every step for the program
 *   none yet     a program GAC Pro can't check automatically still gets an action: its steps in Setup, or the
 *                evidence a person adds
 *
 * Reads only (any member). Nothing is computed here that the server did not send.
 */
import { Link } from 'react-router-dom';
import { nextStepFor } from '../../lib/setup-model';
import { ProgramCoverageBlock, programShort, UnlockChips } from '../setup/parts';
import { useSetupData } from '../setup/use-setup';
import '../setup/setup.css';

export function ProgramStart({ slug, program }: { slug: string; program: string }) {
  const prefix = `/w/${slug}/app`;
  const { enablers, coverage } = useSetupData(slug);
  if (enablers.isLoading || coverage.isLoading) return null;
  // Flow review (High): a failed read still says where the steps are, with Retry.
  if (enablers.error || coverage.error) {
    return <section className="su-card" data-part="program-start"><p className="su-hint">What your sources already cover here couldn't load.</p>
      <div className="su-acts"><button type="button" className="su-btn" onClick={() => { void enablers.refetch(); void coverage.refetch(); }}>Retry</button>
        <Link className="su-linkish" to={`${prefix}/setup?program=${encodeURIComponent(program)}#steps`}>See the steps in Setup</Link></div></section>;
  }
  const list = enablers.data?.enablers ?? [];
  const cov = (coverage.data?.programs ?? []).find((c) => c.program === program && c.objectives_in_scope > 0) ?? null;
  const name = programShort(program);
  const next = nextStepFor(program, list);
  const today = new Date().toISOString().slice(0, 10);
  const stepsHref = `${prefix}/setup?program=${encodeURIComponent(program)}#steps`;
  return (
    <section className="su-card" aria-labelledby="ps-start-h" data-part="program-start">
      <h2 id="ps-start-h" style={{ margin: 0, fontSize: 'var(--fs-lg)' }}>What you already have, and what's next</h2>
      {cov ? <>
        {cov.connected === 0 && <p className="su-hint">{`None of your connected sources reports on a ${name} objective yet.`}</p>}
        <ProgramCoverageBlock cov={cov} enablers={list} status={null} today={today} prefix={prefix} heading={null} legendSeen
          noSourceHref={`${prefix}/setup?evidence=${encodeURIComponent(program)}#nosrc-${encodeURIComponent(program)}`} />
      </> : <p className="su-hint">{`GAC Pro has no ${name} objective it can check automatically yet. Its steps and the evidence a person adds are in Setup.`}</p>}
      {next ? (
        <div className="su-strip-row" data-part="program-start-next">
          <div>
            <span className="su-eyebrow">Next for {name}</span>
            <p className="su-strip-title" style={{ margin: 0 }}>{next.title}</p>
            <p className="su-strip-meta"><UnlockChips step={{ unlocks: { ...next.unlocks, programs: { [program]: next.unlocks.programs[program] ?? [] } } }} /></p>
          </div>
          <div className="su-acts">
            <Link className="su-btn" to={`${prefix}/setup?step=${encodeURIComponent(next.id)}#steps`}>{next.title}<span className="su-sr"> in Setup</span></Link>
            <Link className="su-linkish" to={stepsHref}>{`All ${name} steps`}</Link>
          </div>
        </div>
      ) : <div className="su-acts"><Link className="su-btn" to={stepsHref}>{`See ${name} steps in Setup`}</Link></div>}
    </section>
  );
}
